import './vertical-word-scroll.scss';
import Base from '../base/base';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Configuration options for VerticalWordScroll
 */
interface VerticalWordScrollOptions {
  triggerSelector?: string;
  containerSelector?: string;
  wordWrapSelector?: string;
  wordSelector?: string;
  scrollCompression?: number;
  opacityDuration?: number;
  alignmentPoint?: number;
  scrub?: number;
  ease?: string;
  debug?: boolean;
  [key: string]: any;
}

/**
 * VerticalWordScroll
 * 
 * Animates words vertically as user scrolls, highlighting the word that aligns
 * with a fixed heading. Uses GSAP ScrollTrigger to drive the animation.
 * 
 * @requires gsap
 * @requires ScrollTrigger (GSAP plugin)
 */
export default class VerticalWordScroll extends Base {
  private config: Required<Omit<VerticalWordScrollOptions, 'debug'>>;
  private trigger: HTMLElement | null;
  private container: HTMLElement | null;
  private wordWrap: HTMLElement | null;
  private words: NodeListOf<HTMLElement> | null;
  private scrollTriggerInstance: ScrollTrigger | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private currentActiveIndex: number = 0;
  private wordHeights: number[] = [];
  private wordPositions: number[] = [];

  /**
   * @param options - Configuration options
   * @param options.triggerSelector - Selector for the scroll trigger element (tall container)
   * @param options.containerSelector - Selector for the sticky container
   * @param options.wordWrapSelector - Selector for the word wrapper that translates
   * @param options.wordSelector - Selector for individual words
   * @param options.scrollCompression - Percentage of section height to compress scroll range (0-100)
   * @param options.opacityDuration - Duration for opacity transition in seconds
   * @param options.alignmentPoint - Vertical alignment point as decimal (0.47 = 47% from top)
   * @param options.scrub - ScrollTrigger scrub duration (smoothing/momentum)
   * @param options.ease - GSAP easing for opacity animation
   * @param options.debug - Enable debug logging
   */
  constructor(options: VerticalWordScrollOptions = {}) {
    // Pass debug option to Base class
    super(options.debug || false);

    // Merge user config with defaults
    this.config = {
      triggerSelector: '.onestop__wrapper',
      containerSelector: '.wordscroll-container',
      wordWrapSelector: '.wordscroll__wordwrap',
      wordSelector: '.wordscroll__word',
      scrollCompression: 20,
      opacityDuration: 0.3,
      alignmentPoint: 0.47,
      scrub: 0.5,
      ease: 'power1.out',
      ...options
    };

    // Get DOM references
    this.trigger = document.querySelector(this.config.triggerSelector) as HTMLElement;
    this.container = document.querySelector(this.config.containerSelector) as HTMLElement;
    this.wordWrap = document.querySelector(this.config.wordWrapSelector) as HTMLElement;
    this.words = document.querySelectorAll(this.config.wordSelector);

    // Validate required elements exist
    if (!this.trigger || !this.container || !this.wordWrap || !this.words || this.words.length === 0) {
      console.error('VerticalWordScroll: Required DOM elements not found', {
        trigger: this.config.triggerSelector,
        container: this.config.containerSelector,
        wordWrap: this.config.wordWrapSelector,
        wordSelector: this.config.wordSelector
      });
      return;
    }

    // Initialize state
    this.scrollTriggerInstance = null;
    this.resizeObserver = null;
    this.currentActiveIndex = 0;

    // Bind methods to maintain context
    this.refresh = this.refresh.bind(this);
    this.handleScrollProgress = this.handleScrollProgress.bind(this);

    // Start the show
    this.init();
  }

  /**
   * Initialize the vertical word scroll effect
   */
  init(): void {
    // Set initial opacity for all words
    this.setInitialOpacity();

    // Calculate dimensions and positions
    this.calculateDimensions();

    // Create scroll animation
    this.createScrollAnimation();

    // Set first word to active
    this.setActiveWord(0);

    // Watch for resize events
    this.setupResizeObserver();
  }

  /**
   * Set initial opacity for all words (0.5 except first word)
   */
  setInitialOpacity(): void {
    if (!this.words) return;

    this.words.forEach((word, index) => {
      gsap.set(word, { opacity: index === 0 ? 1 : 0.5 });
    });
  }

  /**
   * Calculate dimensions and positions for word scroll
   * Measures word heights and calculates total scroll distance
   */
  calculateDimensions(): void {
    if (!this.words || !this.wordWrap) return;

    // Reset arrays
    this.wordHeights = [];
    this.wordPositions = [];

    // Get computed styles for gap
    const wordWrapStyles = getComputedStyle(this.wordWrap);
    const gap = parseFloat(wordWrapStyles.rowGap) || parseFloat(wordWrapStyles.gap) || 0;

    // Measure each word and calculate positions
    let cumulativePosition = 0;
    this.words.forEach((word, index) => {
      const height = word.offsetHeight;
      this.wordHeights.push(height);
      this.wordPositions.push(cumulativePosition);
      
      // Add height and gap for next word position
      cumulativePosition += height + gap;
    });
  }

  /**
   * Calculate the total distance words need to travel
   * Last word should end at alignment point
   * 
   * @returns Total scroll distance in pixels
   */
  calculateScrollDistance(): number {
    if (!this.words || this.words.length === 0 || !this.container) return 0;

    // Get container height to calculate alignment point in pixels
    const containerHeight = this.container.offsetHeight;
    const alignmentPixels = containerHeight * this.config.alignmentPoint;

    // First word starts at alignment point (47%)
    // Last word should end at alignment point
    // So we need to scroll the distance between first and last word positions
    const lastWordIndex = this.words.length - 1;
    const scrollDistance = this.wordPositions[lastWordIndex];

    return scrollDistance;
  }

  /**
   * Create GSAP ScrollTrigger animation
   * Maps vertical scroll progress to word translation and opacity
   */
  createScrollAnimation(): void {
    if (!this.trigger || !this.wordWrap) return;

    // Kill existing instance if present
    if (this.scrollTriggerInstance) {
      this.scrollTriggerInstance.kill();
    }

    const scrollDistance = this.calculateScrollDistance();

    // Create vertical translation animation
    const animation = gsap.to(this.wordWrap, {
      y: -scrollDistance,
      ease: 'none' // Linear progression; easing applied via scrub
    });

    // Create ScrollTrigger instance
    this.scrollTriggerInstance = ScrollTrigger.create({
      trigger: this.trigger,
      start: 'top top',
      end: `bottom-=${this.config.scrollCompression}% bottom`,
      scrub: this.config.scrub,
      animation: animation,
      onUpdate: (self) => {
        this.handleScrollProgress(self.progress);
      }
    });
  }

  /**
   * Handle scroll progress updates
   * Determines which word should be active based on scroll position
   * 
   * @param progress - Scroll progress from 0 to 1
   */
  handleScrollProgress(progress: number): void {
    if (!this.words || !this.container) return;

    // Calculate current scroll position in pixels
    const scrollDistance = this.calculateScrollDistance();
    const currentScrollY = progress * scrollDistance;

    // Get container height and alignment point in pixels
    const containerHeight = this.container.offsetHeight;
    const alignmentPixels = containerHeight * this.config.alignmentPoint;

    // Calculate initial wordWrap position (where it starts before any scroll)
    const wordWrapInitialTop = containerHeight * this.config.alignmentPoint;

    // Find which word's center is closest to alignment point
    let closestIndex = 0;
    let smallestDistance = Infinity;

    this.words.forEach((word, index) => {
      // Calculate word's center position accounting for:
      // 1. Initial wordWrap position (CSS top: 47%)
      // 2. Word's position within wordWrap
      // 3. Current scroll translation
      const wordTop = wordWrapInitialTop + this.wordPositions[index] - currentScrollY;
      const wordHeight = this.wordHeights[index];
      const wordCenter = wordTop + (wordHeight / 2);

      // Distance from word center to alignment point
      const distance = Math.abs(wordCenter - alignmentPixels);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestIndex = index;
      }
    });

    // Update active word if changed
    if (closestIndex !== this.currentActiveIndex) {
      this.setActiveWord(closestIndex);
    }
  }

  /**
   * Set the active word with opacity animation
   * 
   * @param index - Index of the word to make active
   */
  setActiveWord(index: number): void {
    if (!this.words) return;

    // Animate previous active word back to 0.5
    if (this.currentActiveIndex !== index) {
      gsap.to(this.words[this.currentActiveIndex], {
        opacity: 0.5,
        duration: this.config.opacityDuration,
        ease: this.config.ease
      });
    }

    // Animate new active word to 1.0
    gsap.to(this.words[index], {
      opacity: 1,
      duration: this.config.opacityDuration,
      ease: this.config.ease
    });

    // Update current active index
    this.currentActiveIndex = index;
  }

  /**
   * Setup ResizeObserver to handle responsive recalculations
   */
  setupResizeObserver(): void {
    // Disconnect existing observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Create new observer
    this.resizeObserver = new ResizeObserver(() => {
      this.refresh();
    });

    // Watch container element for size changes
    if (this.container) {
      this.resizeObserver.observe(this.container);
    }
  }

  /**
   * Refresh the scroll animation
   * Called on resize or when dimensions change
   */
  refresh(): void {
    // Kill existing ScrollTrigger
    if (this.scrollTriggerInstance) {
      this.scrollTriggerInstance.kill();
      this.scrollTriggerInstance = null;
    }

    // Reset any inline transform styles
    if (this.wordWrap) {
      gsap.set(this.wordWrap, { clearProps: 'y' });
    }

    // Recalculate dimensions
    this.calculateDimensions();

    // Recreate animation
    this.createScrollAnimation();

    // Reset active word
    this.setActiveWord(0);
  }

  /**
   * Cleanup method
   * Destroys ScrollTrigger, disconnects observers, clears styles
   */
  destroy(): void {
    // Kill ScrollTrigger instance
    if (this.scrollTriggerInstance) {
      this.scrollTriggerInstance.kill();
      this.scrollTriggerInstance = null;
    }

    // Disconnect ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clear inline styles
    if (this.wordWrap) {
      gsap.set(this.wordWrap, { clearProps: 'y' });
    }

    if (this.words) {
      this.words.forEach(word => {
        gsap.set(word, { clearProps: 'opacity' });
      });
    }
  }
}
